import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useRef } from 'react';
import {
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import { useAgGridCommon } from 'common/table/agGrid/AgGridCommon';
import columnDefs from './columnDefs';
import { commonPaginationOptions, mergeGridOpts } from 'utils/agGrid/options';
import { useAppDispatch } from 'hooks/useAppDispatch';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import {
  fetchEmployeesPromiseCreator,
  selectEmployee,
} from 'reducers/employeesReducer';
import { ServerSideQuery } from 'utils/agGrid/types';
import { employee } from 'constants/userTypes';
import history from 'constants/history';
import { Employee } from 'ts/models/User.model';
import { hideModal, showModal } from 'erisxkit/client';
import { createEmployeePromise } from 'reducers/usersReducer';
import { Button, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { CREATE_MEMBER } from 'constants/modalTypes';
import * as userTypes from 'constants/userTypes';
import { processSSRM } from 'utils/agGrid/ssrm/processSSRM';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  .add-button {
    width: 170px;
    align-self: flex-end;
    margin-bottom: 0px !important;
  }
  > div {
    margin: 10px 5px 10px 5px;
  }
`;
type FetchEmployeesRequest = ServerSideQuery & {
  includeName: string;
};

const gridOptions = mergeGridOpts(commonPaginationOptions, {
  defaultColDef: { floatingFilter: false },
  rowStyle: { cursor: 'pointer' },
});

export const EMPLOYEES_GRID_NAME = 'employees';

const EmployeesGrid = () => {
  const gridRef = useRef<AgGridReact>(null);
  const dispatch = useAppDispatch();

  const { gridVisible, initialGridSettings, onStateUpdated, gridButtons } =
    useAgGridCommon({
      gridName: EMPLOYEES_GRID_NAME,
      gridRef,
    });

  const fetchData = useCallback(
    async (params: IServerSideGetRowsParams) => {
      const { request } = params;
      const body = parseAGRequest(request, { transformAttrs: true });
      const reqBody: FetchEmployeesRequest = {
        ...body,
        filter: [
          ...(body?.filter ? body.filter : []),
          { attr: 'type', op: 'eq', value: employee },
        ],
        includeName: 'true',
      };
      const response = await fetchEmployeesPromiseCreator(reqBody, dispatch);
      return {
        rows: response?.users,
        count: response.count,
      }
    },
    [dispatch],
  );

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) => processSSRM(params, fetchData),
    [fetchData],
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      const dataSource: IServerSideDatasource = {
        getRows,
      };
      params.api?.setGridOption('serverSideDatasource', dataSource);
    },
    [getRows],
  );

  const onRowClicked = useCallback(({ data }: { data: Employee }) => {
    if (data?.userId) {
      selectEmployee(data.userId);
      history.push(`/employees/${data.userId}`);
    }
  }, []);

  const createEmployee = useCallback(
    (payload: { memberUserType: null; email: string }) => {
      createEmployeePromise(payload, dispatch).then(() => {
        if (gridRef?.current) {
          gridRef.current.api.refreshServerSide({ purge: true });
        }
      });
    },
    [dispatch, gridRef],
  );
  const onCreateClick = useCallback(() => {
    dispatch(
      showModal(CREATE_MEMBER, {
        hideModal: hideModal,
        onApply: createEmployee,
        type: userTypes.employee,
        getValuesFromSelector: true,
      }),
    );
  }, [dispatch, createEmployee]);

  return (
    <div className="ag-theme-quartz">
      {gridVisible ? (
        <>
          <Wrapper>
            <Button
              onClick={onCreateClick}
              className="add-button"
              size="small"
              floated="right"
            >
              <Icon name="add" /> Create Employee
            </Button>
            {gridButtons}
          </Wrapper>
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            onStateUpdated={onStateUpdated}
            initialState={initialGridSettings}
            rowModelType="serverSide"
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
          />
        </>
      ) : null}
    </div>
  );
};

export default EmployeesGrid;
